/**
 * PATH: packages/email-hub/src/feature/components/CardJunkControl.tsx
 * FILE_ID: EMAILHUB-CARDJUNKCONTROL-001
 * PURPOSE: Compact, always-visible "Junk ▾" affordance for an INBOX CARD.
 *          One click opens a menu: bin this email now, or arm an
 *          auto-junk rule (retro-sweep existing + auto-bin future mail)
 *          for the whole sender domain or just this sender.
 * WHY:     Operator (2026-06-16): "next to amazon.com where there is an
 *          open card … should have Mark as Junk and then retrospectively
 *          and prospectively mark emails like this as Junk." Junk +
 *          options previously lived only in the reader header
 *          (`JunkButton`) and the right-click menu — never as a visible
 *          card affordance, so an auto-filed promo could only be junked by
 *          opening it or discovering right-click.
 * FLOW:
 *   ┌────────────┐   ┌────────────────────┐   ┌──────────────────────┐
 *   │ click Junk │ → │ portal menu @ btn  │ → │ junkNow / armRule(..) │
 *   └────────────┘   └────────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - ../hooks/useJunkActions: shared junk verbs (same as JunkButton)
 *   - ./MailboxContextMenu: reused body-portal menu (escapes the card's
 *     overflow-hidden clip; viewport-clamped + self-dismissing)
 *   - ../lib/mailboxMenuItems (MailboxMenuItem): generic item shape
 * RELATED:
 *   - ./JunkButton.tsx: the reader-header twin (same useJunkActions)
 *   - packages/ui EmailRow.tsx: renders this via the `junkControl` slot
 */

import { useRef, useState, type MouseEvent } from 'react'
import { AtSign, Ban, ChevronDown, Globe, Loader2 } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { type Email } from '../hooks/useEmails'
import { useJunkActions } from '../hooks/useJunkActions'
import type { MailboxMenuItem } from '../lib/mailboxMenuItems'
import { MailboxContextMenu } from './MailboxContextMenu'

export interface CardJunkControlProps {
  email: Pick<Email, 'id' | 'from_address'>
  /** Refetch the list after a junk/rule mutation so the row drops out. */
  onRefetch?: () => void
  /**
   * Terminal disposition handler. When provided, the host owns dropping the
   * row LOCALLY (and advancing the reader) the moment the junk POST resolves
   * — an instant vanish — instead of falling back to a second full-list
   * `onRefetch()` round-trip. Operator (2026-06-20): the card "Junk took a
   * while to actually action it" precisely because the card only had
   * `onRefetch` (POST → await → refetch → only THEN the row left). Mirrors
   * the reader pane, which has always passed this. See useJunkActions.
   */
  onResolved?: (emailId: string) => void
  /** Disable while a sibling row action is running. */
  disabled?: boolean
  /** Icon-only rendering for the list row's always-visible action cluster —
   *  a single Ban glyph (no "Mark junk" label / chevron) so the cluster stays
   *  a tight row of square icons instead of a wide pill that crowds the chip
   *  strip. The full labelled pill is kept for the reader / wider surfaces. */
  compact?: boolean
}

export function CardJunkControl({
  email,
  onRefetch,
  onResolved,
  disabled = false,
  compact = false,
}: CardJunkControlProps) {
  const { busy, junkNow, armRule, exact, domain, hasOptions } = useJunkActions(
    email,
    onRefetch,
    onResolved,
  )
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  const openMenu = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (busy || disabled) return
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) setMenuAt({ x: rect.left, y: rect.bottom + 4 })
  }

  // Bin-now + the two rule options, built as generic menu items so the
  // reused MailboxContextMenu renders + portals them.
  const items: MailboxMenuItem[] = [
    {
      id: 'junk-now',
      label: 'Junk - bin this email',
      icon: Ban,
      onClick: () => void junkNow(),
    },
  ]
  if (hasOptions) {
    items.push({ kind: 'divider', id: 'div-rules' })
    if (domain) {
      items.push({
        id: 'junk-domain',
        label: `Auto-trash domain · ${domain}`,
        icon: Globe,
        danger: true,
        onClick: () => void armRule('sender_domain'),
      })
    }
    if (exact) {
      items.push({
        id: 'junk-exact',
        label: `Just this sender · ${exact}`,
        icon: AtSign,
        danger: true,
        onClick: () => void armRule('sender_exact'),
      })
    }
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={openMenu}
        disabled={busy || disabled}
        title="Junk - bin now, or auto-trash this sender / domain"
        aria-haspopup="menu"
        aria-label="Mark junk"
        aria-expanded={menuAt !== null}
        data-testid="card-junk-control"
        className={cn(
          compact
            ? // Icon-only: a square glyph button that matches the sibling
              // star/bin in the row's action cluster — 28px on phones, 24px
              // from md↑, with touch-manipulation — kept IN SYNC with
              // RowActions.ACTION_BTN so the junk glyph lines up and is just as
              // tappable as its neighbours (operator 2026-06-20 mis-tap fix).
              cn(
                'inline-flex items-center justify-center h-7 w-7 md:h-6 md:w-6 rounded-md shrink-0 touch-manipulation',
                'text-muted-foreground/45 hover:bg-rose-500/10 hover:text-rose-600',
                'dark:hover:text-rose-300 transition-colors cursor-pointer',
              )
            : cn(
                'inline-flex items-center gap-1 rounded-full border border-border touch-manipulation',
                'px-2 py-0.5 text-[10px] font-medium text-muted-foreground',
                'hover:border-rose-300 hover:bg-rose-500/10 hover:text-rose-600',
                'dark:hover:text-rose-300 transition-colors',
              ),
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          (busy || disabled) && 'opacity-60 cursor-not-allowed',
        )}
      >
        {/* Swap to a spinner the moment the junk POST is in flight so the tap
            reads as ACKNOWLEDGED immediately - the verb resolves async and the
            row only drops once the server confirms, so without this the card
            just sat there (operator 2026-06-20: "took a while to actually
            action it"). Mirrors the reader JunkButton's busy spinner. */}
        {busy ? (
          <Loader2
            className={cn('shrink-0 animate-spin', compact ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5')}
            aria-hidden
          />
        ) : (
          <Ban className={cn('shrink-0', compact ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5')} aria-hidden />
        )}
        {/* A verb, not a noun. "Junk" in a pill read like an APPLIED LABEL
            (operator 2026-06-17: "why is this labelled junk") when it is in
            fact an ACTION on every card. "Mark junk" can't be misread.
            Suppressed in compact mode - the icon + aria-label carry it. */}
        {!compact && <span>Mark junk</span>}
        {!compact && (
          <ChevronDown className="h-2.5 w-2.5 shrink-0 text-muted-foreground/60" aria-hidden />
        )}
      </button>
      {menuAt && (
        <MailboxContextMenu
          x={menuAt.x}
          y={menuAt.y}
          items={items}
          ariaLabel="Junk options"
          onClose={() => setMenuAt(null)}
        />
      )}
    </>
  )
}

export default CardJunkControl
